import type { ReactNode } from "react";
import { Icon, type IconName } from "../components/Icon";
import { catalogTracks, firstContentPart, resolveContentPart } from "../lib/catalog";
import { contentHash, resumeHash, routeHash } from "../lib/routing.mjs";
import { summarizeProgress } from "../lib/summary";
import { useLanguage } from "../app/useStore";
import type { Selection } from "../types";

type CatalogRoute = Selection & { page: "home" | "catalog" | "part" };

const Card = ({ title, copy, href, glyph = "book" }: { title: string; copy: string; href: string; glyph?: IconName }) => (
  <a className="catalog-card" href={href}>
    <span className="track-icon"><Icon name={glyph} /></span>
    <div><h2>{title}</h2><p>{copy}</p></div>
    <Icon name="next" />
  </a>
);

/** Language home → track → collection → part hub. Study, Audio Practice and Stories live inside a part. */
export function CatalogPage({ route }: { route: CatalogRoute }) {
  const ctx = useLanguage();
  const pack = ctx.fullPack;
  const languageId = pack.definition.id;
  const tracks = catalogTracks(pack);
  const track = tracks.find((t) => t.id === route.trackId);
  const collection = track?.collections.find((c) => c.id === route.collectionId && c.available !== false);
  const part = route.partId ? resolveContentPart(pack, route) : null;
  const link = (s: Selection, page: "catalog" | "part" = "catalog") => contentHash(languageId, s, page);
  const last = ctx.progress.lastActivity;

  const continuation = last && (
    <a className="catalog-continue" href={resumeHash(languageId, last, firstContentPart(pack))}>
      <div><span className="overline">Pick up where you left off</span><h2>{last.label || "Your practice"}</h2></div>
      <span>Continue <Icon name="arrow" /></span>
    </a>
  );

  const s = summarizeProgress(pack, ctx.progress, part ? route : null);
  const summary = (
    <section className="shared-progress">
      <div className="section-title"><h2>Every encounter adds up</h2><a className="text-link" href={ctx.href("stats")}>Explore stats <Icon name="arrow" /></a></div>
      <div className="summary-metrics">
        <div><strong>{s.encountered}<small> / {s.words}</small></strong><span>Words encountered</span></div>
        <div><strong>{s.exposures.toLocaleString()}</strong><span>Total exposures</span></div>
        <div><strong>{s.reviewed}</strong><span>Words reviewed</span></div>
        <div><strong>{s.audio.toLocaleString()}</strong><span>Listening encounters</span></div>
      </div>
      <p className="hint">Study, listening, and stories contribute to the same word history.</p>
    </section>
  );

  let body: ReactNode;
  if (part) {
    const modes: [Parameters<typeof ctx.href>[0], string, string, IconName][] = [
      ["study", "Study", "Flip through the words with spaced review, or take a recognition round.", "book"],
      ["audio", "Audio Practice", "Listen, repeat, and recall aloud while you walk.", "audio"],
      ["stories", "Stories", "Hear familiar words in short stories. Follow the text and explore definitions.", "stories"],
    ];
    body = (
      <>
        <a className="back-link" href={link({ trackId: part.track.id, collectionId: part.collection.id })}><Icon name="back" /> {part.collection.title}</a>
        <div className="view-heading">
          <div>
            <p className="overline">{part.track.title} · {part.collection.title}</p>
            <h1>{part.part.title}</h1>
            <p>One vocabulary set. Three ways to make it familiar.</p>
          </div>
          <span className="count-badge">{part.deck.length} vocabulary entries</span>
        </div>
        {last && (!last.partId || last.partId === part.part.id) && continuation}
        {summary}
        <div className="section-title"><h2>Choose how to practice</h2><span>Shared vocabulary, shared progress</span></div>
        <div className="catalog-modes">{modes.map(([page, title, copy, glyph]) => <Card key={page} title={title} copy={copy} href={ctx.href(page)} glyph={glyph} />)}</div>
        <p className="footnote">Stories reinforce this collection’s words and include supporting vocabulary.</p>
      </>
    );
  } else if (track) {
    const shown = collection ? [collection] : track.collections.filter((c) => c.available !== false);
    body = (
      <>
        <a className="back-link" href={collection ? link({ trackId: track.id }) : routeHash(languageId)}><Icon name="back" /> {collection ? track.title : pack.definition.name}</a>
        <div className="view-heading"><div>
          <p className="overline">{pack.definition.name} · {track.title}</p>
          <h1>{collection?.title || track.title}</h1>
          <p>{track.description || "Choose a collection to begin."}</p>
        </div></div>
        {shown.map((c) => (
          <section className="catalog-collection" key={c.id}>
            {!collection && <div className="section-title"><h2><a href={link({ trackId: track.id, collectionId: c.id })}>{c.title}</a></h2></div>}
            <div className="catalog-list">
              {c.parts.filter((p) => p.available !== false).map((p) => {
                const scoped = resolveContentPart(pack, { trackId: track.id, collectionId: c.id, partId: p.id })!;
                return <Card key={p.id} title={p.title} copy={`${scoped.deck.length} vocabulary entries · Study, Audio Practice, Stories`} href={link({ trackId: track.id, collectionId: c.id, partId: p.id }, "part")} />;
              })}
            </div>
          </section>
        ))}
      </>
    );
  } else {
    body = (
      <>
        <div className="view-heading">
          <div>
            <p className="overline">Your language</p>
            <h1>{pack.definition.name}</h1>
            <p>Choose a learning track. Your progress grows with every encounter.</p>
          </div>
          <span className="home-language" lang={languageId}>{pack.definition.nativeName}</span>
        </div>
        {continuation}
        <div className="section-title"><h2>Learning tracks</h2></div>
        <div className="catalog-list">
          {tracks.map((t) => <Card key={t.id} title={t.title} copy={t.description || "Explore this track."} href={link({ trackId: t.id })} />)}
        </div>
        {summary}
      </>
    );
  }
  return <>{body}</>;
}
